<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { useAnnotationFormState } from "~/stores/useAnnotationFormState";
import { Paragraph } from "~/types/Paragraph";

const props = defineProps({
  paragraph: {
    type: Object as PropType<Paragraph>,
    required: true,
  },
  tokenGroup: {
    type: Object as PropType<ParagraphTokens>,
    required: true,
  },
  isBeforeParagraph: {
    type: Boolean,
    default: false,
  },
});

const documentId = inject("documentId") as any;
const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const documentAnnotaitonStore = useDocumentAnnotationsStore(documentId);
const { allAnnotations } = storeToRefs(documentAnnotaitonStore);

// Paragraph annotations are those where the ParagraphId on the annotation is equal to this paragraph
// We now also attach end line to the paragraphId but we do not want to show these here
// Instead we want isEndLine annotation to appear atached to the last token of the paragraph
const paragraphAnnotations = computed(() => {
  return useParagraphAnnotations(
    allAnnotations.value,
    props.paragraph,
    props.tokenGroup,
    props.isBeforeParagraph
  );
});

const longTokenAnnotations = computed(() => {
  return useLongTokenAnnotations(
    allAnnotations.value,
    props.paragraph,
    props.tokenGroup
  );
});

const showAddAnnotation = ref(false);
const show = () => {
  showAddAnnotation.value = true;
};

const hide = () => {
  showAddAnnotation.value = false;
};

const { setAnnotationBeingEdited, createDefaultAnnotation } =
  useAnnotationFormState();

const documentSelectionStore = useDocumentSelectionStore(documentId);

const addAnnotation = () => {
  if (isTeacherViewMode.value != true) return;

  documentSelectionStore.clearSelection();

  const annotation = createDefaultAnnotation(
    AnnotationTypes.commentOnly,
    props.paragraph.id
  );

  annotation.isBeforeParagraph = props.isBeforeParagraph ?? false;

  // We only want to assign a lastTokenGlobalPosition if this paragraph has tokens.
  if (props.tokenGroup.length > 0) {
    annotation.lastTokenGlobalPosition =
      props.tokenGroup[props.tokenGroup.length - 1]?.globalPosition;
  }

  setAnnotationBeingEdited(documentId, annotation);
};

const onDoubleClick = () => {
  documentSelectionStore.clearSelection();

  useCreateInlineEditingAnnotation(
    documentId,
    props.paragraph.id,
    false,
    undefined,
    false,
    props.isBeforeParagraph
  );
};
</script>

<template>
  <span class="flex flex-col not-exportable">
    <span class="text-danger-default text-left flex flex-col indent-0">
      <span>
        <DocumentInlineAnnotation
          v-for="annotation in longTokenAnnotations"
          :key="(annotation.id ?? '') + annotation.lastUpdatedTimestamp"
          :annotation="annotation"
          :show-token-text="true"
          class="mx-[4px] selection:bg-none shrink-0"
        />
      </span>

      <DocumentInlineAnnotation
        v-for="annotation in paragraphAnnotations"
        :key="(annotation.id ?? '') + annotation.lastUpdatedTimestamp"
        :annotation="annotation"
        :show-token-text="true"
        class="mx-[4px] selection:bg-none block"
      />
    </span>
    <span
      class="relative"
      v-if="isTeacherViewMode == true"
      :data-step-id="`paragraph-annotations-${paragraph.id}`"
      @click="addAnnotation"
    >
      <BaseClickHandler
        @click="addAnnotation"
        @dblclick="onDoubleClick"
        :data-step-id="`paragraph-add-annotation-${paragraph.id}`"
      >
        <BaseDelayedMouseover
          @mouseenter="show"
          @mouseleave="hide"
          :class="{
            'hover:bg-light-default cursor-pointer': showAddAnnotation == true,
          }"
        >
          <div
            class="hover:bg-light-default rounded text-center select-none text-medium-default cursor-pointer transition-opacity relative text-[8px]"
            :class="{
              'opacity-0': showAddAnnotation != true,
              'opacity-100': showAddAnnotation == true,
            }"
          >
            Add Comment
          </div>
        </BaseDelayedMouseover>
      </BaseClickHandler>
    </span>
  </span>
</template>

<style scoped></style>
